import liff from "@line/liff";
import type { MessengerPlatform, UserProfile } from "./types";

export interface CreateLIFFPlatformOptions {
  liffId: string;
}

export interface CreateMockPlatformOptions {
  token?: string;
  profile?: Partial<UserProfile>;
}

/**
 * E2E / ローカル検証用: LINE SDK (`liff.init`/`liff.login`) を呼ばずに
 * MessengerPlatform を返すモック。`liff.login()` のリダイレクトを避けるため、
 * driver アプリの起動経路 (main.tsx) で E2E フラグ時に使う。
 * セッションが localStorage にあれば getAccessToken は呼ばれないが、念のため固定値を返す。
 */
export function createMockPlatform(options: CreateMockPlatformOptions = {}): MessengerPlatform {
  const token = options.token ?? "e2e-mock-access-token";
  const profile: UserProfile = {
    userId: options.profile?.userId ?? "e2e-mock-user",
    displayName: options.profile?.displayName ?? "E2E ドライバー",
    pictureUrl: options.profile?.pictureUrl,
  };
  return {
    type: "liff",
    async init() {},
    async getAccessToken() {
      return token;
    },
    async getProfile() {
      return profile;
    },
    isInClient() {
      return false;
    },
    closeWindow() {},
    openExternalUrl(url: string) {
      if (typeof window !== "undefined") window.open(url, "_blank");
    },
  };
}

export async function createLIFFPlatform({
  liffId,
}: CreateLIFFPlatformOptions): Promise<MessengerPlatform> {
  await liff.init({ liffId });
  if (!liff.isLoggedIn()) {
    liff.login();
    // リダイレクトが発生するので以降の処理は到達しない
  }
  return {
    type: "liff",
    async init() {
      // 既に init 済み
    },
    async getAccessToken() {
      const token = liff.getAccessToken();
      if (!token) throw new Error("LIFF access token unavailable");
      return token;
    },
    async getProfile(): Promise<UserProfile> {
      const profile = await liff.getProfile();
      return {
        userId: profile.userId,
        displayName: profile.displayName,
        pictureUrl: profile.pictureUrl,
      };
    },
    isInClient() {
      return liff.isInClient();
    },
    closeWindow() {
      liff.closeWindow();
    },
    openExternalUrl(url: string) {
      liff.openWindow({ url, external: true });
    },
  };
}
